/**
 * WishlistPickerMenu — replaces the orphan "Track" button on search
 * results. Tap → dropdown of the user's wishlists + "+ New wishlist".
 * Pick one → product gets added to that wishlist (POST /products with
 * wishlist_id).
 *
 * Why: previously Track POSTed without a wishlist_id, so the product
 * landed in tracked_products with wishlist_id = NULL — orphan rows that
 * have no listing surface in the AWL UI. This forces the user to put
 * each tracked item somewhere they can find it later.
 *
 * Internal-tool simplifications:
 *  - "+ New wishlist" uses window.prompt() for naming. Good enough for
 *    the team UI; replace with a Dialog if/when this hits external users.
 *  - Once a product is added to ANY wishlist, the button locks to
 *    "Added to <name>" for the session. Adding to multiple wishlists
 *    isn't supported from this surface; use the wishlist detail page.
 */

import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, Loader2, Plus, Star } from 'lucide-react';
import { useWishlists, useCreateWishlist, Wishlist } from '../hooks/useWishlists';
import { useCreateProduct } from '../hooks/useProducts';
import { CatalogSearchResult } from '../hooks/useCatalogSearch';

interface Props {
  result: CatalogSearchResult;
}

export function WishlistPickerMenu({ result }: Props) {
  const { data: wishlists, isLoading: wishlistsLoading } = useWishlists();
  const createWishlist = useCreateWishlist();
  const createProduct = useCreateProduct();
  const [addedTo, setAddedTo] = useState<string | null>(null); // wishlist name once added
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function addTo(wishlist: Wishlist) {
    setPending(true);
    setError(null);
    try {
      await createProduct.mutateAsync({
        name: result.name || 'Untitled Product',
        normalized_url: result.normalized_url,
        wishlist_id: wishlist.id,
      });
      setAddedTo(wishlist.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add');
    } finally {
      setPending(false);
    }
  }

  async function addToNewWishlist() {
    const name = window.prompt('New wishlist name:', 'My Wishlist');
    if (!name || !name.trim()) return;
    setPending(true);
    setError(null);
    try {
      const wl = await createWishlist.mutateAsync(name.trim());
      await createProduct.mutateAsync({
        name: result.name || 'Untitled Product',
        normalized_url: result.normalized_url,
        wishlist_id: wl.id,
      });
      setAddedTo(wl.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add');
    } finally {
      setPending(false);
    }
  }

  // Once added, lock the button (for this session). Avoids the user
  // accidentally double-adding to the same wishlist; dedup is handled
  // server-side too but this is faster feedback.
  if (addedTo) {
    return (
      <button
        type="button"
        disabled
        className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow disabled:opacity-50"
        title={`Added to ${addedTo}`}
      >
        Added to {addedTo.length > 14 ? addedTo.slice(0, 14) + '…' : addedTo}
      </button>
    );
  }

  return (
    <div className="flex-1">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            disabled={pending || wishlistsLoading}
            className="w-full inline-flex items-center justify-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          >
            {pending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <>
                <Plus className="h-3 w-3" />
                Add to wishlist
                <ChevronDown className="h-3 w-3 -mr-1" />
              </>
            )}
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            sideOffset={4}
            align="start"
            className="z-50 min-w-[200px] rounded-md border border-border bg-popover text-popover-foreground shadow-lg p-1 max-h-[300px] overflow-y-auto"
          >
            {wishlists && wishlists.length > 0 ? (
              <>
                {wishlists.map((wl) => (
                  <DropdownMenu.Item
                    key={wl.id}
                    onSelect={() => addTo(wl)}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground outline-none"
                  >
                    <span className="truncate flex items-center gap-1">
                      {wl.is_default && <Star className="h-3 w-3 text-amber-500 shrink-0" />}
                      {wl.name}
                    </span>
                    {wl.product_count != null && (
                      <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                        {wl.product_count}
                      </span>
                    )}
                  </DropdownMenu.Item>
                ))}
                <DropdownMenu.Separator className="my-1 h-px bg-border" />
              </>
            ) : null}
            <DropdownMenu.Item
              onSelect={addToNewWishlist}
              className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground outline-none"
            >
              <Plus className="h-3 w-3" />
              New wishlist…
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {error && (
        <p className="text-[10px] text-destructive mt-1 truncate" title={error}>
          {error}
        </p>
      )}
    </div>
  );
}
